import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Plus,
  Trash2
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import procurementApproverMasterService from '../../services/procurementApproverMasterService'
import { getUsers } from '../../services/projectService'
import supplierMasterService from '../../services/supplierMasterService'
import categoryMasterService from '../../services/categoryMasterService'

const ProcurementApproverMasterForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const approverData = location.state?.approverData

  const [formData, setFormData] = useState({
    approverCode: '',
    approverName: '',
    approver: '',
    approverEmail: '',
    approverDesignation: '',
    approverDepartment: '',
    approvalLevel: 1,
    parentApprover: '',
    approvalLimits: {
      singleTransactionLimit: 0,
      singleTransactionCurrency: 'INR',
      monthlyLimit: 0,
      monthlyCurrency: 'INR',
      annualLimit: 0,
      annualCurrency: 'INR'
    },
    approvalScope: {
      categories: [],
      productTypes: [],
      serviceTypes: [],
      suppliers: [],
      allCategories: false,
      allSuppliers: false
    },
    approvalRules: {
      requireMultipleApprovals: false,
      minimumApprovalsRequired: 1,
      escalationEnabled: false,
      escalationAfterHours: 24,
      escalationTo: '',
      canDelegate: false,
      delegateTo: []
    },
    notificationSettings: {
      emailNotifications: true,
      smsNotifications: false,
      pushNotifications: true
    },
    status: 'Active',
    notes: ''
  })

  const [users, setUsers] = useState([])
  const [approvers, setApprovers] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [categories, setCategories] = useState([])
  const [isLoadingApproverCode, setIsLoadingApproverCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [newCategory, setNewCategory] = useState('')
  const [newSupplier, setNewSupplier] = useState('')

  // Fetch next approver code (only for new approvers)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchApproverCode = async () => {
        try {
          setIsLoadingApproverCode(true)
          const response = await procurementApproverMasterService.getNextApproverCode()
          if (response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              approverCode: response.data.approverCode || response.data.formattedValue || 'PAPPR0001'
            }))
          }
        } catch (error) {
          console.error('Error fetching approver code:', error)
          setFormData(prev => ({ ...prev, approverCode: 'PAPPR0001' }))
        } finally {
          setIsLoadingApproverCode(false)
        }
      }
      fetchApproverCode()
    }
  }, [isEditMode, isViewMode])

  // Load users, approvers, suppliers, and categories
  useEffect(() => {
    loadUsers()
    loadApprovers()
    loadSuppliers()
    loadCategories()
  }, [])

  // Load approver data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && approverData?._id) {
      loadApproverData(approverData._id)
    }
  }, [isEditMode, isViewMode, approverData])

  const loadUsers = async () => {
    try {
      const response = await getUsers()
      if (response.success) {
        setUsers(response.data || [])
      }
    } catch (error) {
      console.error('Error loading users:', error)
    }
  }

  const loadApprovers = async () => {
    try {
      const response = await procurementApproverMasterService.getAllProcurementApprovers({ status: 'Active', limit: 1000 })
      if (response.success) {
        setApprovers(response.data || [])
      }
    } catch (error) {
      console.error('Error loading approvers:', error)
    }
  }

  const loadSuppliers = async () => {
    try {
      const response = await supplierMasterService.getAllSuppliers({ status: 'Active', limit: 1000 })
      if (response.success) {
        setSuppliers(response.data || [])
      }
    } catch (error) {
      console.error('Error loading suppliers:', error)
    }
  }

  const loadCategories = async () => {
    try {
      const response = await categoryMasterService.getAllCategories({ status: 'Active', limit: 1000 })
      if (response.success) {
        setCategories(response.data || [])
      }
    } catch (error) {
      console.error('Error loading categories:', error)
    }
  }

  const loadApproverData = async (id) => {
    try {
      setIsLoading(true)
      const response = await procurementApproverMasterService.getProcurementApproverById(id)
      if (response.success && response.data) {
        const approver = response.data
        setFormData({
          approverCode: approver.approverCode || '',
          approverName: approver.approverName || '',
          approver: approver.approver?._id || approver.approver || '',
          approverEmail: approver.approverEmail || '',
          approverDesignation: approver.approverDesignation || '',
          approverDepartment: approver.approverDepartment || '',
          approvalLevel: approver.approvalLevel || 1,
          parentApprover: approver.parentApprover?._id || approver.parentApprover || '',
          approvalLimits: approver.approvalLimits || {
            singleTransactionLimit: 0,
            singleTransactionCurrency: 'INR',
            monthlyLimit: 0,
            monthlyCurrency: 'INR',
            annualLimit: 0,
            annualCurrency: 'INR'
          },
          approvalScope: approver.approvalScope || {
            categories: [],
            productTypes: [],
            serviceTypes: [],
            suppliers: [],
            allCategories: false,
            allSuppliers: false
          },
          approvalRules: approver.approvalRules || {
            requireMultipleApprovals: false,
            minimumApprovalsRequired: 1,
            escalationEnabled: false,
            escalationAfterHours: 24,
            escalationTo: '',
            canDelegate: false,
            delegateTo: []
          },
          notificationSettings: approver.notificationSettings || {
            emailNotifications: true,
            smsNotifications: false,
            pushNotifications: true
          },
          status: approver.status || 'Active',
          notes: approver.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading approver data:', error)
      toast.error('Failed to load approver data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('approvalLimits.')) {
      const limitField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        approvalLimits: {
          ...prev.approvalLimits,
          [limitField]: type === 'number' ? parseFloat(value) || 0 : value
        }
      }))
    } else if (name.startsWith('approvalScope.')) {
      const scopeField = name.split('.')[1]
      if (scopeField === 'categories' || scopeField === 'productTypes' || scopeField === 'serviceTypes' || scopeField === 'suppliers') {
        // Handle array fields - this would need special handling for multi-select
        // For now, keeping as is
        setFormData(prev => ({
          ...prev,
          approvalScope: {
            ...prev.approvalScope,
            [scopeField]: value
          }
        }))
      } else {
        setFormData(prev => ({
          ...prev,
          approvalScope: {
            ...prev.approvalScope,
            [scopeField]: type === 'checkbox' ? checked : value
          }
        }))
      }
    } else if (name.startsWith('approvalRules.')) {
      const ruleField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        approvalRules: {
          ...prev.approvalRules,
          [ruleField]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
        }
      }))
    } else if (name.startsWith('notificationSettings.')) {
      const notifField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        notificationSettings: {
          ...prev.notificationSettings,
          [notifField]: checked
        }
      }))
    } else if (name === 'approver') {
      const selectedUser = users.find(u => u._id === value)
      setFormData(prev => ({
        ...prev,
        approver: value,
        approverName: selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}`.trim() : prev.approverName,
        approverEmail: selectedUser?.email || prev.approverEmail,
        approverDesignation: selectedUser?.designation || prev.approverDesignation,
        approverDepartment: selectedUser?.department || prev.approverDepartment
      }))
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const addCategory = () => {
    if (!newCategory) return
    if (!formData.approvalScope.categories.includes(newCategory)) {
      setFormData(prev => ({
        ...prev,
        approvalScope: {
          ...prev.approvalScope,
          categories: [...prev.approvalScope.categories, newCategory]
        }
      }))
      setNewCategory('')
    }
  }

  const removeCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      approvalScope: {
        ...prev.approvalScope,
        categories: prev.approvalScope.categories.filter(c => c !== category)
      }
    }))
  }

  const addSupplier = () => {
    if (!newSupplier) return
    if (!formData.approvalScope.suppliers.includes(newSupplier)) {
      setFormData(prev => ({
        ...prev,
        approvalScope: {
          ...prev.approvalScope,
          suppliers: [...prev.approvalScope.suppliers, newSupplier]
        }
      }))
      setNewSupplier('')
    }
  }

  const removeSupplier = (supplierId) => {
    setFormData(prev => ({
      ...prev,
      approvalScope: {
        ...prev.approvalScope,
        suppliers: prev.approvalScope.suppliers.filter(s => s !== supplierId)
      }
    }))
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.approverName || !formData.approver) {
        toast.error('Please fill in all required fields (Approver Name and User)')
        return
      }

      setIsLoading(true)
      
      // Convert empty strings to null
      const submitData = {
        ...formData,
        parentApprover: formData.parentApprover || null,
        approvalRules: {
          ...formData.approvalRules,
          escalationTo: formData.approvalRules.escalationTo || null
        }
      }
      
      if (isEditMode && approverData?._id) {
        const response = await procurementApproverMasterService.updateProcurementApprover(approverData._id, submitData)
        if (response.success) {
          toast.success('Procurement approver updated successfully!')
          navigate('/procurement/procurement-approver-master')
        } else {
          toast.error(response.message || 'Failed to update procurement approver')
        }
      } else {
        const response = await procurementApproverMasterService.createProcurementApprover(submitData)
        if (response.success) {
          toast.success('Procurement approver created successfully!')
          navigate('/procurement/procurement-approver-master')
        } else {
          toast.error(response.message || 'Failed to create procurement approver')
        }
      }
    } catch (error) {
      console.error('Error saving procurement approver:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save procurement approver')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (isEditMode || isViewMode) {
      loadApproverData(approverData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        approverName: '',
        approver: '',
        approverEmail: '',
        approverDesignation: '',
        approverDepartment: '',
        approvalLevel: 1,
        parentApprover: '',
        approvalLimits: {
          singleTransactionLimit: 0,
          singleTransactionCurrency: 'INR',
          monthlyLimit: 0,
          monthlyCurrency: 'INR',
          annualLimit: 0,
          annualCurrency: 'INR'
        },
        approvalScope: {
          categories: [],
          productTypes: [],
          serviceTypes: [],
          suppliers: [],
          allCategories: false,
          allSuppliers: false
        },
        approvalRules: {
          requireMultipleApprovals: false,
          minimumApprovalsRequired: 1,
          escalationEnabled: false,
          escalationAfterHours: 24,
          escalationTo: '',
          canDelegate: false,
          delegateTo: []
        },
        notificationSettings: {
          emailNotifications: true,
          smsNotifications: false,
          pushNotifications: true
        },
        status: 'Active',
        notes: ''
      }))
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Procurement', path: '/procurement' },
    { label: 'Approvers', path: '/procurement/procurement-approver-master' },
    { 
      label: isEditMode ? 'Edit Approver' : isViewMode ? 'View Approver' : 'New Approver', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading approver...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Approver' : isViewMode ? 'View Approver' : 'New Approver'}</h1>
          <p>{isEditMode ? 'Update approver information' : isViewMode ? 'View approver information' : 'Create a new procurement approver'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/procurement/procurement-approver-master')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Approver Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>Basic Information</h3></div>
          {/* Row 1: Approver Code, Approver Name, User, Status */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="approverCode" className="form-label required">
                Approver Code
              </label>
              <input
                type="text"
                id="approverCode"
                name="approverCode"
                value={isLoadingApproverCode ? 'Generating...' : formData.approverCode}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="approverName" className="form-label required">
                Approver Name
              </label>
              <input
                type="text"
                id="approverName"
                name="approverName"
                value={formData.approverName}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
                placeholder="Approver name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="approver" className="form-label required">
                User
              </label>
              <select
                id="approver"
                name="approver"
                value={formData.approver}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
              >
                <option value="">Select User</option>
                {users.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName} ({user.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Row 2: Email, Designation, Department */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="approverEmail" className="form-label">
                Email
              </label>
              <input
                type="email"
                id="approverEmail"
                name="approverEmail"
                value={formData.approverEmail}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="approver@example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="approverDesignation" className="form-label">
                Designation
              </label>
              <input
                type="text"
                id="approverDesignation"
                name="approverDesignation"
                value={formData.approverDesignation}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Designation"
              />
            </div>

            <div className="form-group">
              <label htmlFor="approverDepartment" className="form-label">
                Department
              </label>
              <input
                type="text"
                id="approverDepartment"
                name="approverDepartment"
                value={formData.approverDepartment}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Department"
              />
            </div>
          </div>

          {/* Approval Hierarchy */}
          <div className="section-header"><h3>Approval Hierarchy</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="approvalLevel" className="form-label">
                Approval Level
              </label>
              <input
                type="number"
                id="approvalLevel"
                name="approvalLevel"
                value={formData.approvalLevel}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="1"
              />
            </div>

            <div className="form-group">
              <label htmlFor="parentApprover" className="form-label">
                Parent Approver
              </label>
              <select
                id="parentApprover"
                name="parentApprover"
                value={formData.parentApprover}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="">None (Top Level)</option>
                {approvers
                  .filter(app => !isEditMode || app._id !== approverData?._id)
                  .map(approver => (
                    <option key={approver._id} value={approver._id}>
                      {approver.approverName} (Level {approver.approvalLevel})
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Approval Limits */}
          <div className="section-header"><h3>Approval Limits</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="approvalLimits.singleTransactionLimit" className="form-label">
                Single Transaction Limit
              </label>
              <input
                type="number"
                id="approvalLimits.singleTransactionLimit"
                name="approvalLimits.singleTransactionLimit"
                value={formData.approvalLimits.singleTransactionLimit}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="approvalLimits.singleTransactionCurrency" className="form-label">
                Currency
              </label>
              <select
                id="approvalLimits.singleTransactionCurrency"
                name="approvalLimits.singleTransactionCurrency"
                value={formData.approvalLimits.singleTransactionCurrency}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="approvalLimits.monthlyLimit" className="form-label">
                Monthly Limit
              </label>
              <input
                type="number"
                id="approvalLimits.monthlyLimit"
                name="approvalLimits.monthlyLimit"
                value={formData.approvalLimits.monthlyLimit}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="approvalLimits.annualLimit" className="form-label">
                Annual Limit
              </label>
              <input
                type="number"
                id="approvalLimits.annualLimit"
                name="approvalLimits.annualLimit"
                value={formData.approvalLimits.annualLimit}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          {/* Approval Scope */}
          <div className="section-header"><h3>Approval Scope</h3></div>

          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                checked={formData.approvalScope.allCategories}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  approvalScope: { ...prev.approvalScope, allCategories: e.target.checked }
                }))}
                disabled={isViewMode}
                style={{ width: '18px', height: '18px', marginRight: '8px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
              />
              Approve All Categories
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                checked={formData.approvalScope.allSuppliers}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  approvalScope: { ...prev.approvalScope, allSuppliers: e.target.checked }
                }))}
                disabled={isViewMode}
                style={{ width: '18px', height: '18px', marginRight: '8px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
              />
              Approve All Suppliers
            </label>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="approvalScope.productTypes" className="form-label">
                Product Types
              </label>
              <select
                id="approvalScope.productTypes"
                name="approvalScope.productTypes"
                value={formData.approvalScope.productTypes[0] || ''}
                onChange={(e) => {
                  if (e.target.value && !formData.approvalScope.productTypes.includes(e.target.value)) {
                    setFormData(prev => ({
                      ...prev,
                      approvalScope: {
                        ...prev.approvalScope,
                        productTypes: [...prev.approvalScope.productTypes, e.target.value]
                      }
                    }))
                  }
                }}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="">Select Product Type</option>
                <option value="Goods">Goods</option>
                <option value="Service">Service</option>
                <option value="Software">Software</option>
                <option value="Hardware">Hardware</option>
                <option value="Consumable">Consumable</option>
                <option value="Asset">Asset</option>
                <option value="Other">Other</option>
              </select>
              {formData.approvalScope.productTypes.length > 0 && (
                <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {formData.approvalScope.productTypes.map((type, idx) => (
                    <span key={idx} className="status-badge active" style={{ fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      {type}
                      {!isViewMode && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            approvalScope: {
                              ...prev.approvalScope,
                              productTypes: prev.approvalScope.productTypes.filter((_, i) => i !== idx)
                            }
                          }))}
                          style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, marginLeft: '4px' }}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="approvalScope.serviceTypes" className="form-label">
                Service Types
              </label>
              <select
                id="approvalScope.serviceTypes"
                name="approvalScope.serviceTypes"
                value={formData.approvalScope.serviceTypes[0] || ''}
                onChange={(e) => {
                  if (e.target.value && !formData.approvalScope.serviceTypes.includes(e.target.value)) {
                    setFormData(prev => ({
                      ...prev,
                      approvalScope: {
                        ...prev.approvalScope,
                        serviceTypes: [...prev.approvalScope.serviceTypes, e.target.value]
                      }
                    }))
                  }
                }}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="">Select Service Type</option>
                <option value="Consulting">Consulting</option>
                <option value="Maintenance">Maintenance</option>
                <option value="Professional">Professional</option>
                <option value="IT Services">IT Services</option>
                <option value="Support">Support</option>
                <option value="Training">Training</option>
                <option value="Other">Other</option>
              </select>
              {formData.approvalScope.serviceTypes.length > 0 && (
                <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {formData.approvalScope.serviceTypes.map((type, idx) => (
                    <span key={idx} className="status-badge active" style={{ fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      {type}
                      {!isViewMode && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            approvalScope: {
                              ...prev.approvalScope,
                              serviceTypes: prev.approvalScope.serviceTypes.filter((_, i) => i !== idx)
                            }
                          }))}
                          style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, marginLeft: '4px' }}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Approval Rules */}
          <div className="section-header"><h3>Approval Rules</h3></div>

          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                checked={formData.approvalRules.requireMultipleApprovals}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  approvalRules: { ...prev.approvalRules, requireMultipleApprovals: e.target.checked }
                }))}
                disabled={isViewMode}
                style={{ width: '18px', height: '18px', marginRight: '8px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
              />
              Require Multiple Approvals
            </label>
          </div>

          {formData.approvalRules.requireMultipleApprovals && (
            <div className="form-group">
              <label htmlFor="approvalRules.minimumApprovalsRequired" className="form-label">
                Minimum Approvals Required
              </label>
              <input
                type="number"
                id="approvalRules.minimumApprovalsRequired"
                name="approvalRules.minimumApprovalsRequired"
                value={formData.approvalRules.minimumApprovalsRequired}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="1"
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                checked={formData.approvalRules.escalationEnabled}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  approvalRules: { ...prev.approvalRules, escalationEnabled: e.target.checked }
                }))}
                disabled={isViewMode}
                style={{ width: '18px', height: '18px', marginRight: '8px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
              />
              Enable Escalation
            </label>
          </div>

          {formData.approvalRules.escalationEnabled && (
            <div className="grid-row-4">
              <div className="form-group">
                <label htmlFor="approvalRules.escalationAfterHours" className="form-label">
                  Escalate After (Hours)
                </label>
                <input
                  type="number"
                  id="approvalRules.escalationAfterHours"
                  name="approvalRules.escalationAfterHours"
                  value={formData.approvalRules.escalationAfterHours}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="approvalRules.escalationTo" className="form-label">
                  Escalate To
                </label>
                <select
                  id="approvalRules.escalationTo"
                  name="approvalRules.escalationTo"
                  value={formData.approvalRules.escalationTo}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                >
                  <option value="">Select Approver</option>
                  {approvers
                    .filter(app => !isEditMode || app._id !== approverData?._id)
                    .map(approver => (
                      <option key={approver._id} value={approver._id}>
                        {approver.approverName} (Level {approver.approvalLevel})
                      </option>
                    ))}
                </select>
              </div>
            </div>
          )}

          {/* Notification Settings */}
          <div className="section-header"><h3>Notification Settings</h3></div>

          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                name="notificationSettings.emailNotifications"
                checked={formData.notificationSettings.emailNotifications}
                onChange={handleInputChange}
                disabled={isViewMode}
                style={{ width: '18px', height: '18px', marginRight: '8px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
              />
              Email Notifications
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                name="notificationSettings.smsNotifications"
                checked={formData.notificationSettings.smsNotifications}
                onChange={handleInputChange}
                disabled={isViewMode}
                style={{ width: '18px', height: '18px', marginRight: '8px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
              />
              SMS Notifications
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                name="notificationSettings.pushNotifications"
                checked={formData.notificationSettings.pushNotifications}
                onChange={handleInputChange}
                disabled={isViewMode}
                style={{ width: '18px', height: '18px', marginRight: '8px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
              />
              Push Notifications
            </label>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Additional notes..."
            />
          </div>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="btn btn-primary"
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="btn btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default ProcurementApproverMasterForm

